/* eslint-disable react-hooks/rules-of-hooks */
import {
  FocusEvent,
  forwardRef,
  HTMLAttributes,
  KeyboardEvent,
  memo,
  MouseEvent,
  ReactNode,
  RefAttributes,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import Menu, { MenuProps } from "@mui/material/Menu";
import { MenuItemProps } from "@mui/material/MenuItem";

import ChevronRight from "@zenith/assets/icons/ChevronRight";
import IconMenuItem from "./IconMenuItem/icon-menu-item";

/**
 * Props for Nested Menu Item Component.
 * @typedef
 * @name NestedMenuItemProps
 * @kind variable
 * @exports
 */
export type NestedMenuItemProps = Omit<MenuItemProps, "button"> & {
  /**
   * If `true`, opens parent menu.
   * @default false
   * @property
   * @name parentMenuOpen
   * @type {boolean}
   */
  parentMenuOpen: boolean;
  /**
   * (optional)
   * Label for the Icon Menu Item.
   * @property
   * @name label
   * @type {string | undefined}
   */
  label?: string;
  /**
   * (optional)
   * Icon on the right.
   * @default <ChevronRight />
   * @property
   * @name rightIcon
   * @type {ReactNode}
   */
  rightIcon?: ReactNode;
  /**
   * (optional)
   * Icon on the left.
   * @default null
   * @property
   * @name leftIcon
   * @type {ReactNode}
   */
  leftIcon?: ReactNode;
  /**
   * Children element.
   * @property
   * @name children
   * @type {ReactNode}
   */
  children?: ReactNode;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @type {string | undefined}
   */
  className?: string;
  /**
   * (optional)
   * `tabIndex` attribute necessary for keyboard navigation.
   * @property
   * @name tabIndex
   * @type {number | undefined}
   */
  tabIndex?: number;
  /**
   * (optional)
   * If `true`, disables the `tabIndex`.
   * @property
   * @name disabled
   * @type {boolean | undefined}
   */
  disabled?: boolean;
  /**
   * Container Props.
   * @property
   * @name ContainerProps
   * @type {function}
   */
  ContainerProps?: HTMLAttributes<HTMLElement> &
    RefAttributes<HTMLElement | null>;
  /**
   * Menu Props.
   * @property
   * @name MenuProps
   * @type {Partial<Omit<MenuProps, "children">> | undefined}
   */
  MenuProps?: Partial<Omit<MenuProps, "children">>;
  /**
   * (optional)
   * Count of items filtered.
   * @property
   * @name filterCount
   * @type {number | undefined}
   */
  filterCount?: number;
};

/**
 * Memoized Nested Menu Item Component.
 * @constant
 * @name NestedMenuItemComponent
 * @kind variable
 * @type {React.MemoExoticComponent<React.ForwardRefExoticComponent<Omit<NestedMenuItemProps, "ref"> & RefAttributes<HTMLLIElement | null>>>}
 * @exports
 */
export const NestedMenuItemComponent: React.MemoExoticComponent<
  React.ForwardRefExoticComponent<
    Omit<NestedMenuItemProps, "ref"> & RefAttributes<HTMLLIElement | null>
  >
> = memo(
  forwardRef<HTMLLIElement | null, NestedMenuItemProps>(
    /**
     * Nested Menu Item Component.
     * @function
     * @name NestedMenuItem
     * @kind function
     * @memberof NestedMenuItemComponent
     * @param {NestedMenuItemProps} props
     * @param {React.ForwardedRef<HTMLLIElement | null>} ref
     * @returns {JSX.Element}
     */
    function NestedMenuItem(
      props: NestedMenuItemProps,
      ref: React.ForwardedRef<HTMLLIElement | null>
    ): JSX.Element {
      const {
        parentMenuOpen,
        label,
        rightIcon = <ChevronRight />,
        leftIcon = null,
        children,
        className,
        tabIndex: tabIndexProp,
        ContainerProps: ContainerPropsProp = {},
        MenuProps,
        filterCount,
        ...MenuItemProps
      } = props;

      const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

      const menuItemRef = useRef<HTMLLIElement | null>(null);
      useImperativeHandle(ref, () => menuItemRef.current!); // eslint-disable-line @typescript-eslint/no-non-null-assertion

      const containerRef = useRef<HTMLDivElement | null>(null);
      if (containerRefProp && typeof containerRefProp !== "string") {
        useImperativeHandle(containerRefProp, () => containerRef.current);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const menuContainerRef = useRef<HTMLDivElement | null | any>(null);

      const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

      const handleMouseEnter = (e: MouseEvent<HTMLElement>) => {
        setIsSubMenuOpen(true);

        if (ContainerProps.onMouseEnter) {
          ContainerProps.onMouseEnter(e);
        }
      };
      const handleMouseLeave = (e: MouseEvent<HTMLElement>) => {
        setIsSubMenuOpen(false);

        if (ContainerProps.onMouseLeave) {
          ContainerProps.onMouseLeave(e);
        }
      };

      // Check if any immediate children are active
      const isSubmenuFocused = () => {
        const active =
          containerRef.current?.ownerDocument.activeElement ?? null;
        for (const child of menuContainerRef.current.children) {
          if (child === active) {
            return true;
          }
        }

        return false;
      };

      const handleFocus = (e: FocusEvent<HTMLElement>) => {
        if (e.target === containerRef.current) {
          setIsSubMenuOpen(true);
        }

        if (ContainerProps.onFocus) {
          ContainerProps.onFocus(e);
        }
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          return;
        }

        if (isSubmenuFocused()) {
          e.stopPropagation();
        }

        const active = containerRef.current?.ownerDocument.activeElement;

        if (e.key === "ArrowLeft" && isSubmenuFocused()) {
          containerRef.current?.focus();
        }

        if (
          e.key === "ArrowRight" &&
          e.target === containerRef.current &&
          e.target === active
        ) {
          const firstChild = menuContainerRef.current
            ?.children[0] as HTMLDivElement;
          firstChild?.focus();
        }
      };

      const open = isSubMenuOpen && parentMenuOpen;

      // Root element must have a `tabIndex` attribute for keyboard navigation
      let tabIndex;
      if (!props.disabled) {
        tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
      }

      return (
        <div
          {...ContainerProps}
          ref={containerRef}
          onFocus={handleFocus}
          tabIndex={tabIndex}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onKeyDown={handleKeyDown}
        >
          <IconMenuItem
            MenuItemProps={MenuItemProps}
            className={className}
            ref={menuItemRef}
            leftIcon={leftIcon}
            rightIcon={rightIcon}
            label={label}
            filterCount={filterCount}
          />

          <Menu
            // Set pointer events to 'none' to prevent the invisible Popover div
            // from capturing events for clicks and hovers
            style={{ pointerEvents: "none" }}
            anchorEl={menuItemRef.current}
            anchorOrigin={{
              horizontal: "right",
              vertical: "top",
            }}
            transformOrigin={{
              horizontal: "left",
              vertical: "top",
            }}
            open={open}
            autoFocus={false}
            disableAutoFocus
            disableEnforceFocus
            onClose={() => {
              setIsSubMenuOpen(false);
            }}
            {...MenuProps}
          >
            <div ref={menuContainerRef} style={{ pointerEvents: "auto" }}>
              {children}
            </div>
          </Menu>
        </div>
      );
    }
  )
);

export default NestedMenuItemComponent;
